import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useHalachicData } from '../../hooks/useHalachicData';
import { buildLimudimItems, toShowKey } from './limudimUtils';
import { LIMUDIM_PROGRAMS } from '../../engine/providers/learningProvider';
import templates from './templates';

/**
 * LimudimWidget
 * מציג את סדרי הלימוד היומיים — דף יומי, משנה יומית, רמב"ם, נ"ך יומי ועוד.
 * כל סדר ניתן להפעלה/כיבוי בנפרד.
 * מתעדכן אוטומטית בשקיעה ובחצות (דרך useHalachicData).
 */
export const LimudimWidget = ({
  edah = 'ashkenaz',
  title = 'לימודים יומיים',
  showTitle = true,
  template = 'Sefer',
  location,
  // Individual program show flags — default: main 3 on, rest off
  showDafYomi = true,
  showMishnaYomi = true,
  showNachYomi = false,
  showRambam1 = true,
  showRambam3 = false,
  showYerushalmiVilna = false,
  showYerushalmiSchottenstein = false,
  showChofetzChaim = false,
  showShemiratHaLashon = false,
  showSeferHaMitzvot = false,
  showKitzurShulchanAruch = false,
  showArukhHaShulchanYomi = false,
  showPsalms = false,
  showTanakhYomi = false,
  showPirkeiAvotSummer = false,
  showPerekYomi = false,
  showDafWeekly = false,
  showDirshuAmudYomi = false,
  // Preview mode
  previewItems = null,
  previewHebrewDate = null,
}) => {
  const Template = templates[template] || templates['Sefer'];
  const isPreview = !!previewItems;

  const engineConfig = useMemo(() => ({
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: edah === 'mizrahi' ? 'mizrahi' : edah === 'sephardic' ? 'sephardic' : 'ashkenaz',
  }), [location?.lat, location?.lng, location?.timezone, location?.tzid, edah]);

  const { today, loading, error } = useHalachicData(engineConfig, { autoFetch: !isPreview });

  const settings = useMemo(() => ({
    showDafYomi, showMishnaYomi, showNachYomi, showRambam1, showRambam3,
    showYerushalmiVilna, showYerushalmiSchottenstein,
    showChofetzChaim, showShemiratHaLashon, showSeferHaMitzvot,
    showKitzurShulchanAruch, showArukhHaShulchanYomi,
    showPsalms, showTanakhYomi, showPirkeiAvotSummer, showPerekYomi,
    showDafWeekly, showDirshuAmudYomi,
  }), [
    showDafYomi, showMishnaYomi, showNachYomi, showRambam1, showRambam3,
    showYerushalmiVilna, showYerushalmiSchottenstein,
    showChofetzChaim, showShemiratHaLashon, showSeferHaMitzvot,
    showKitzurShulchanAruch, showArukhHaShulchanYomi,
    showPsalms, showTanakhYomi, showPirkeiAvotSummer, showPerekYomi,
    showDafWeekly, showDirshuAmudYomi,
  ]);

  const { items, groups } = useMemo(() => {
    if (isPreview || !today?.limudim) return { items: [], groups: [] };
    return buildLimudimItems(today.limudim, settings);
  }, [isPreview, today, settings]);

  // Preview mode
  if (isPreview) {
    return (
      <Template
        today={{ hebrewDate: { rendered: previewHebrewDate || '' } }}
        items={previewItems}
        groups={null}
        loading={false}
        error={null}
        title={title}
        showTitle={showTitle}
        edah={edah}
      />
    );
  }

  if (loading && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: '#1e3a5f' }} />
      </Box>
    );
  }

  if (error && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>שגיאה בטעינת הנתונים</Typography>
      </Box>
    );
  }

  return (
    <Template
      today={today}
      items={items}
      groups={groups}
      loading={loading}
      error={error}
      title={title}
      showTitle={showTitle}
      edah={edah}
    />
  );
};
